import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { getOrganizationMenu, getSections } from '../helpers/Api';
import { verificarLojaAberta } from '../../utils/lojaUtils';

const MenuContext = createContext(null);

const VAREJO_THEME_KEY = 'varejo_theme';

export function MenuProvider({ children }) {
  const [menuData, setMenuData] = useState({
    orgData: null,
    sections: [],
    loading: false,
    currentSlug: null
  });

  // Tema dark/light exclusivo da vitrine /loja (EmpresaVarejo, EmpresaVarejoSecoes,
  // SecoesVarejoDetalhes, SelectProductVarejoModal) — persistido em localStorage
  // pra manter a escolha do visitante ao navegar entre essas telas.
  const [varejoTheme, setVarejoTheme] = useState(
    () => localStorage.getItem(VAREJO_THEME_KEY) || 'light'
  );

  const toggleVarejoTheme = useCallback(() => {
    setVarejoTheme((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem(VAREJO_THEME_KEY, next);
      return next;
    });
  }, []);

  // Usa refs para acessar estado atual sem recriar a funcao fetchMenu
  const menuDataRef = useRef(menuData);
  menuDataRef.current = menuData;

  // Timestamp do ultimo carregamento, para evitar refetch desnecessario.
  const lastFetchRef = useRef(null);

  // Funcao centralizada para carregar tudo de uma vez
  const fetchMenu = useCallback(async (slug, forceRefresh = false) => {
    const current = menuDataRef.current;
    // Se ja temos os dados dessa empresa e nao for um refresh forcado, nao faz nada
    if (current.currentSlug === slug && current.orgData && !forceRefresh) {
      return;
    }

    setMenuData(prev => ({ ...prev, loading: true }));
    lastFetchRef.current = Date.now();

    try {
      // Chamadas em paralelo para otimizar o tempo de resposta
      const [orgRes, sectionsRes] = await Promise.all([
        getOrganizationMenu(slug),
        getSections(slug)
      ]);

      setMenuData({
        orgData: orgRes.data.data,
        sections: sectionsRes.data.data,
        currentSlug: slug,
        loading: false
      });
    } catch (error) {
      console.error("Erro ao carregar Contexto de Menu:", error);
      setMenuData(prev => ({ ...prev, loading: false }));
    }
  }, []); // sem dependencias — funcao estavel

  // Função para limpar os dados ao trocar de loja (Contorna o Risco 3)
  const clearMenu = useCallback(() => {
    setMenuData({ orgData: null, sections: [], loading: false, currentSlug: null });
    lastFetchRef.current = null;
  }, []);

  // Refetch automatico quando a aba volta ao foco apos ficar em background.
  // Evita que uma pagina aberta ontem a noite continue exibindo "Loja fechada"
  // depois que o horario de funcionamento ja comecou no dia seguinte.
  useEffect(() => {
    const refetchIfNeeded = () => {
      const current = menuDataRef.current;
      const slug = current.currentSlug;
      if (!slug || document.visibilityState !== 'visible') return;

      const agora = Date.now();
      const cincoMinutos = 5 * 60 * 1000;
      const lojaPareceFechada =
        current.orgData && !verificarLojaAberta(current.orgData).aberta;

      const deveRefetch =
        !lastFetchRef.current ||
        agora - lastFetchRef.current > cincoMinutos ||
        lojaPareceFechada;

      if (deveRefetch) {
        fetchMenu(slug, true);
      }
    };

    const handleVisibility = () => refetchIfNeeded();
    const handleFocus = () => refetchIfNeeded();

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchMenu]);

  return (
    <MenuContext.Provider
      value={{ ...menuData, fetchMenu, clearMenu, varejoTheme, toggleVarejoTheme }}
    >
      {children}
    </MenuContext.Provider>
  );
}

export const useMenu = () => {
  const context = useContext(MenuContext);
  if (!context) throw new Error('useMenu deve ser usado dentro de um MenuProvider');
  return context;
};